/**
 * SupportCaseQueue
 * ---------------------------------------------------------------------------
 * The support case queue. One screen for Lusuku staff AND for vendor managers:
 * both logins land here, and the SERVER decides what each one sees. Nothing in
 * this file branches on who is signed in to decide which rows to render, and
 * every field is read optionally, so a scoped payload that arrives without
 * `sla`, without `vendor`, or without `assignment` renders a thinner row rather
 * than throwing.
 *
 * Built out of:
 *   - a summary strip from GET /api/support-cases/stats/summary, counts by
 *     status and by priority plus the SLA breach count. Each tile is a filter.
 *   - a filter bar carrying exactly the list query params the contract names.
 *   - a paginated list with the SLA state on every row: the due time, and a
 *     breach marker driven by sla.breachedFirstResponse / sla.breachedResolution.
 *   - SupportCaseDetailPanel for one case.
 *   - SupportCaseCreateForm behind the "Log a case" action, which is also how a
 *     staff member logs a platform incident. `platformAdmin`, decided once just
 *     below, is the ONE staff-versus-vendor-manager read in this feature: the
 *     form is handed the same flag rather than deciding again.
 *   - a realtime subscription on the rooms this dashboard already joins.
 *
 * REALTIME. Copied from the pattern the existing views use, not invented here:
 *   - room join and re-join on reconnect: WalletDashboard.js (`joinAdmin`) and
 *     VendorMediaAdmin.js / Adverts/AdvertManager.js (`joinSuperadminRoom`).
 *   - the vendor room and the disconnect banner: OmnichannelInbox.js
 *     (`joinVendorInbox` with { vendorId }, and the connect/disconnect effect
 *     that re-joins and silently refetches).
 *   - the leave on unmount mirrors Tables/RidersTable.js, which pairs
 *     `joinAdmin` with `socket.emit("leave", "adminRoom")`.
 * Every `socket.on` in here has a matching `socket.off` in the cleanup, with the
 * same named handler reference, so a remount never stacks a second listener.
 *
 * The queue says "case". `MessagingTicket` and event box-office ticketing are
 * both something else in this repo.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import socket from "../utilities/socket";
import { getUserFromLocalStorage } from "../utilities/user";
import {
  apiListSupportCases,
  apiSupportCaseOptions,
  apiSupportCaseSummary,
  supportCaseErrorMessage,
} from "../api/supportCases";
import {
  CASE_PRIORITIES,
  CASE_SORTS,
  CASE_STATUSES,
  DEFAULT_CASE_PAGE_SIZE,
  EMPTY_CASE_FILTERS,
  SHIPPED_CASE_VOCABULARY,
  buildCaseListQuery,
  caseAssigneeTypeLabel,
  caseCategoryLabel,
  casePriorityLabel,
  casePriorityTone,
  caseSortLabel,
  caseStatusLabel,
  caseStatusTone,
  describeCaseSla,
  hasActiveCaseFilters,
  isTerminalCaseStatus,
  mergeCaseVocabulary,
  normalizeCaseListPayload,
  normalizeCasePayload,
  normalizeCaseSummaryPayload,
  toCaseOptions,
} from "../utilities/supportCaseVocabulary";
import SupportCaseCreateForm from "./SupportCaseCreateForm";
import SupportCaseDetailPanel from "./SupportCaseDetailPanel";
import "./SupportCaseQueue.css";
import AppSelect from "../components/ui/AppSelect";

/** The six events the contract names. Nothing else is subscribed. */
const SUPPORT_CASE_EVENTS = [
  "supportCase:created",
  "supportCase:updated",
  "supportCase:message",
  "supportCase:assigned",
  "supportCase:escalated",
  "supportCase:resolved",
];

// Same two helpers OmnichannelInbox.js uses to read the cached profile, kept
// local for the same reason it does: they are two lines and importing a view
// into a view would be worse.
const resolveVendorId = (profile) =>
  profile?.result?.parentVendorId || profile?.result?._id || profile?.result?.id;

const adminRoles = new Set(["SUPERUSER", "SUPERADMIN", "ADMIN"]);

const isPlatformAdmin = (profile) => {
  const result = profile?.result || {};
  return (
    Boolean(result.isSuperAdmin) ||
    result.userType === "LUSUKU STAFF" ||
    adminRoles.has(result.user_role) ||
    adminRoles.has(result.role)
  );
};

const CaseChip = ({ tone, children }) => (
  <span className={`support-case-chip tone-${tone}`}>{children}</span>
);

const StatTile = ({ label, value, tone = "muted", active = false, onClick }) => (
  <button
    type="button"
    className={`support-case-stat tone-${tone}${active ? " is-active" : ""}`}
    onClick={onClick}
  >
    <div className="support-case-stat-value">{Number(value || 0).toLocaleString()}</div>
    <div className="support-case-stat-label">{label}</div>
  </button>
);

/**
 * The SLA cell. Shows the due time and, whenever the server has flagged a
 * breach, an unmissable marker. Reads `sla.breachedFirstResponse` and
 * `sla.breachedResolution` only: the clock arithmetic here is presentation, the
 * breach itself is the server's decision.
 */
const SlaCell = ({ supportCase, now }) => {
  const sla = describeCaseSla(supportCase, now);
  if (!sla.known && !sla.breached) {
    return <span className="support-case-meta">Not tracked</span>;
  }
  return (
    <div className="support-case-sla">
      {sla.dueAt ? (
        <span className="support-case-sla-due">{moment(sla.dueAt).format("MMM D, h:mm A")}</span>
      ) : null}
      <CaseChip tone={sla.tone}>{sla.label}</CaseChip>
      {sla.breached ? <div className="support-case-breach-flag">SLA breached</div> : null}
    </div>
  );
};

const SupportCaseQueue = () => {
  const profile = getUserFromLocalStorage();
  const platformAdmin = isPlatformAdmin(profile);
  const vendorId = platformAdmin ? "" : resolveVendorId(profile);

  // State before any memo that reads it: a memo declared above its state is the
  // no-use-before-define this repo's lint gate fails on.
  const [vocabulary, setVocabulary] = useState(SHIPPED_CASE_VOCABULARY);
  const [filters, setFilters] = useState(EMPTY_CASE_FILTERS);
  const [searchText, setSearchText] = useState("");
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(() => normalizeCaseSummaryPayload(null));
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [detailSignal, setDetailSignal] = useState(0);
  const [socketDown, setSocketDown] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Re-derives every row's countdown once a minute without refetching.
  const [now, setNow] = useState(() => Date.now());

  const statusOptions = useMemo(
    () => toCaseOptions(vocabulary.statuses, caseStatusLabel),
    [vocabulary.statuses]
  );
  const categoryOptions = useMemo(
    () => toCaseOptions(vocabulary.categories, caseCategoryLabel),
    [vocabulary.categories]
  );
  const priorityOptions = useMemo(
    () => toCaseOptions(vocabulary.priorities, casePriorityLabel),
    [vocabulary.priorities]
  );
  const assigneeTypeOptions = useMemo(
    () => toCaseOptions(vocabulary.assigneeTypes, caseAssigneeTypeLabel),
    [vocabulary.assigneeTypes]
  );

  const query = useMemo(() => buildCaseListQuery(filters), [filters]);
  const filtersActive = useMemo(() => hasActiveCaseFilters(filters), [filters]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((Number(total) || 0) / (query.limit || DEFAULT_CASE_PAGE_SIZE))),
    [total, query.limit]
  );

  // Live mirror of the query so the socket handlers can refetch the CURRENT view
  // without the subscription re-binding on every keystroke in the filter bar.
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const selectedCaseIdRef = useRef(selectedCaseId);
  useEffect(() => {
    selectedCaseIdRef.current = selectedCaseId;
  }, [selectedCaseId]);

  const refreshTimerRef = useRef(null);

  // --- Loading -----------------------------------------------------------

  const loadCases = useCallback(
    async (options = {}) => {
      const params = options.params || queryRef.current;
      if (!options.silent) setLoading(true);
      setError("");
      try {
        const response = await apiListSupportCases(params);
        const page = normalizeCaseListPayload(response?.data);
        setCases(page.cases);
        setTotal(page.total);
        setHasMore(page.hasMore);
      } catch (loadError) {
        if (!options.silent) {
          setError(supportCaseErrorMessage(loadError, "Failed to load the case queue."));
          setCases([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (!options.silent) setLoading(false);
      }
    },
    []
  );

  const loadSummary = useCallback(async () => {
    try {
      const response = await apiSupportCaseSummary();
      setSummary(normalizeCaseSummaryPayload(response?.data));
    } catch (summaryError) {
      // The strip is a convenience. A failure leaves the last good numbers in
      // place and never blocks the queue itself.
      setSummary((previous) => previous);
    }
  }, []);

  // Live vocabulary once per mount. The shipped fallback in
  // utilities/supportCaseVocabulary.js is already in state, so a failure here
  // leaves every dropdown usable instead of empty.
  useEffect(() => {
    let cancelled = false;
    apiSupportCaseOptions()
      .then((response) => {
        if (!cancelled) setVocabulary(mergeCaseVocabulary(response?.data));
      })
      .catch(() => {
        if (!cancelled) setVocabulary(mergeCaseVocabulary(null));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadCases({ params: query });
  }, [query, loadCases]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const ticker = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(ticker);
  }, []);

  // Typing in the search box must not fire a request per character.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((previous) =>
        previous.q === searchText ? previous : { ...previous, q: searchText, page: 1 }
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // --- Realtime ----------------------------------------------------------

  /**
   * One trailing refresh for a burst of events. A resolve, for instance, emits
   * both supportCase:resolved and supportCase:updated: this collapses them into
   * a single silent refetch of the list and the summary.
   */
  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadCases({ silent: true, params: queryRef.current });
      loadSummary();
    }, 600);
  }, [loadCases, loadSummary]);

  /**
   * Patch a row in place when the event carried the whole case and that case is
   * already on screen, so a live update does not reorder or flash the list.
   * Returns whether the patch landed; the caller falls back to a refetch.
   */
  const patchCaseInList = useCallback((updated) => {
    if (!updated?._id) return false;
    let found = false;
    setCases((previous) =>
      previous.map((row) => {
        if (String(row._id) !== String(updated._id)) return row;
        found = true;
        return { ...row, ...updated };
      })
    );
    return found;
  }, []);

  useEffect(() => {
    const joinRooms = () => {
      // The rooms the dashboard already joins. Staff get the platform rooms, a
      // vendor manager gets their own vendor inbox room, and the server decides
      // which of those it actually emits a given case into.
      if (platformAdmin) {
        socket.emit("joinAdmin");
        socket.emit("joinSuperadminRoom");
      }
      if (vendorId) socket.emit("joinVendorInbox", { vendorId });
    };

    const onSupportCaseEvent = (envelope) => {
      const updated = normalizeCasePayload(envelope?.case || envelope?.payload || envelope);
      const eventCaseId =
        envelope?.caseId || envelope?.payload?.caseId || updated?._id || null;

      // The open panel refetches itself rather than being patched from a socket
      // payload, so the thread, the history and the redaction always come from
      // the server rather than from a partial broadcast.
      if (eventCaseId && String(eventCaseId) === String(selectedCaseIdRef.current)) {
        setDetailSignal((tick) => tick + 1);
      }

      const patched = updated ? patchCaseInList(updated) : false;
      if (!patched) {
        // A new case, or one that moved into the current filter, needs the
        // server's ordering and paging. Refetch instead of guessing.
        scheduleSilentRefresh();
      } else {
        loadSummary();
      }
    };

    const onConnect = () => {
      setSocketDown(false);
      joinRooms();
      loadCases({ silent: true, params: queryRef.current });
      loadSummary();
    };
    const onDisconnect = () => setSocketDown(true);

    joinRooms();
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    SUPPORT_CASE_EVENTS.forEach((event) => socket.on(event, onSupportCaseEvent));
    if (!socket.connected) setSocketDown(true);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      SUPPORT_CASE_EVENTS.forEach((event) => socket.off(event, onSupportCaseEvent));
      if (platformAdmin) {
        socket.emit("leave", "adminRoom");
        socket.emit("leaveSuperadminRoom");
      }
      if (vendorId) socket.emit("leaveVendorInbox", { vendorId });
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [platformAdmin, vendorId, loadCases, loadSummary, patchCaseInList, scheduleSilentRefresh]);

  // --- Filter helpers ----------------------------------------------------

  const setFilter = useCallback((key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value, page: 1 }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_CASE_FILTERS);
    setSearchText("");
  }, []);

  const toggleStatusFilter = useCallback((status) => {
    setFilters((previous) => ({
      ...previous,
      status: previous.status === status ? "" : status,
      page: 1,
    }));
  }, []);

  const togglePriorityFilter = useCallback((priority) => {
    setFilters((previous) => ({
      ...previous,
      priority: previous.priority === priority ? "" : priority,
      page: 1,
    }));
  }, []);

  const toggleBreachedFilter = useCallback(() => {
    setFilters((previous) => ({ ...previous, breached: !previous.breached, page: 1 }));
  }, []);

  const goToPage = useCallback((page) => {
    setFilters((previous) => ({ ...previous, page: Math.max(1, page) }));
  }, []);

  const openCase = useCallback((id) => setSelectedCaseId(id || null), []);

  const closeCase = useCallback(() => setSelectedCaseId(null), []);

  const onCaseChanged = useCallback(
    (updated) => {
      const patched = updated ? patchCaseInList(updated) : false;
      if (!patched) loadCases({ silent: true, params: queryRef.current });
      loadSummary();
    },
    [patchCaseInList, loadCases, loadSummary]
  );

  const openCreate = useCallback(() => setShowCreate(true), []);

  const closeCreate = useCallback(() => setShowCreate(false), []);

  /**
   * A freshly logged case. Refetch rather than splicing the row in: the server
   * owns the ordering, the paging and whether the new case even matches the
   * current filters. Then open it, so logging a case lands the person in the
   * case they just logged.
   */
  const onCaseCreated = useCallback(
    (created) => {
      setShowCreate(false);
      loadCases({ params: queryRef.current });
      loadSummary();
      if (created?._id) setSelectedCaseId(created._id);
    },
    [loadCases, loadSummary]
  );

  // Statuses the strip shows as tiles: the live vocabulary, minus the ones that
  // mean the case is off the queue, so the strip stays about work in hand.
  const stripStatuses = useMemo(() => {
    const list = Array.isArray(vocabulary.statuses) ? vocabulary.statuses : CASE_STATUSES;
    return list.filter((status) => !isTerminalCaseStatus(status));
  }, [vocabulary.statuses]);

  const stripPriorities = useMemo(
    () => (Array.isArray(vocabulary.priorities) ? vocabulary.priorities : CASE_PRIORITIES),
    [vocabulary.priorities]
  );

  return (
    <Container fluid className="support-case-page py-3">
      <div className="support-case-shell">
        <div className="support-case-hero">
          <div>
            <div className="support-case-eyebrow">Support</div>
            <h2>Case queue</h2>
            <p>
              Every issue raised on an order, a delivery, a payment or a vendor, with its SLA
              state. You see the cases your account is scoped to.
            </p>
          </div>
          <div className="support-case-hero-actions">
            <Button variant="primary" size="sm" onClick={openCreate}>
              Log a case
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={() => {
                loadCases({ params: queryRef.current });
                loadSummary();
              }}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </div>
        </div>

        {socketDown ? (
          <div className="support-case-offline">
            Live updates are reconnecting. The queue is still readable, and it resyncs on its own
            once the connection is back.
          </div>
        ) : null}

        {/* --- Summary strip ----------------------------------------------- */}
        <div className="support-case-stat-group-title">By status</div>
        <div className="support-case-summary">
          <StatTile
            label="All cases"
            value={summary.total}
            tone="info"
            active={!filtersActive}
            onClick={resetFilters}
          />
          {stripStatuses.map((status) => (
            <StatTile
              key={status}
              label={caseStatusLabel(status)}
              value={summary.byStatus[status] || 0}
              tone={caseStatusTone(status)}
              active={filters.status === status}
              onClick={() => toggleStatusFilter(status)}
            />
          ))}
          <StatTile
            label="SLA breached"
            value={summary.breached}
            tone="urgent"
            active={filters.breached === true}
            onClick={toggleBreachedFilter}
          />
        </div>

        <div className="support-case-stat-group-title">By priority</div>
        <div className="support-case-summary">
          {stripPriorities.map((priority) => (
            <StatTile
              key={priority}
              label={casePriorityLabel(priority)}
              value={summary.byPriority[priority] || 0}
              tone={casePriorityTone(priority)}
              active={filters.priority === priority}
              onClick={() => togglePriorityFilter(priority)}
            />
          ))}
        </div>

        {/* --- Filters ----------------------------------------------------- */}
        <div className="support-case-filters">
          <Row className="g-2">
            <Col md={3} sm={6}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Case number, subject or order number"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Status</Form.Label>
              <AppSelect block
                size="sm"
                value={filters.status}
                onChange={(event) => setFilter("status", event.target.value)}
              >
                <option value="">Any status</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppSelect>
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Category</Form.Label>
              <AppSelect block
                size="sm"
                value={filters.category}
                onChange={(event) => setFilter("category", event.target.value)}
              >
                <option value="">Any category</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppSelect>
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Priority</Form.Label>
              <AppSelect block
                size="sm"
                value={filters.priority}
                onChange={(event) => setFilter("priority", event.target.value)}
              >
                <option value="">Any priority</option>
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppSelect>
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Assigned to</Form.Label>
              <AppSelect block
                size="sm"
                value={filters.assigneeType}
                onChange={(event) => setFilter("assigneeType", event.target.value)}
              >
                <option value="">Anyone</option>
                {assigneeTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppSelect>
            </Col>
            <Col md={1} sm={6}>
              <Form.Label>Sort</Form.Label>
              <AppSelect block
                size="sm"
                value={filters.sort}
                onChange={(event) => setFilter("sort", event.target.value)}
              >
                {CASE_SORTS.map((sort) => (
                  <option key={sort} value={sort}>
                    {caseSortLabel(sort)}
                  </option>
                ))}
              </AppSelect>
            </Col>
          </Row>

          <Row className="g-2" style={{ marginTop: 6 }}>
            <Col md={2} sm={6}>
              <Form.Label>From</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.from}
                onChange={(event) => setFilter("from", event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>To</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.to}
                onChange={(event) => setFilter("to", event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Order id</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Optional"
                value={filters.orderId}
                onChange={(event) => setFilter("orderId", event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Vendor id</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Optional"
                value={filters.vendorId}
                onChange={(event) => setFilter("vendorId", event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Assignee id</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Optional"
                value={filters.assignedTo}
                onChange={(event) => setFilter("assignedTo", event.target.value)}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label>Page size</Form.Label>
              <AppSelect block
                size="sm"
                value={String(filters.limit)}
                onChange={(event) => setFilter("limit", Number(event.target.value))}
              >
                {[20, 50, 100].map((size) => (
                  <option key={size} value={String(size)}>
                    {size} per page
                  </option>
                ))}
              </AppSelect>
            </Col>
          </Row>

          <div className="support-case-filter-actions">
            <Form.Check
              type="switch"
              id="support-case-breached-filter"
              label="Only cases that breached their SLA"
              checked={filters.breached === true}
              onChange={toggleBreachedFilter}
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetFilters}
              disabled={!filtersActive}
            >
              Clear filters
            </Button>
          </div>
        </div>

        {/* --- List -------------------------------------------------------- */}
        {error ? (
          <Card body className="mb-3" style={{ borderColor: "#f3b1ab" }}>
            <span style={{ color: "#a02a1e", fontWeight: 600 }}>{error}</span>
          </Card>
        ) : null}

        <div className="support-case-list">
          <Table hover responsive className="support-case-table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Subject</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>SLA</th>
                <th>Order</th>
                <th>Vendor</th>
                <th>Assigned</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {loading && !cases.length ? (
                <tr>
                  <td colSpan={10} className="support-case-empty">
                    <Spinner animation="border" size="sm" /> Loading cases
                  </td>
                </tr>
              ) : null}

              {!loading && !cases.length ? (
                <tr>
                  <td colSpan={10} className="support-case-empty">
                    {filtersActive
                      ? "No cases match these filters."
                      : "No support cases yet. Anything a customer reports, and anything you log here, will land in this queue."}
                  </td>
                </tr>
              ) : null}

              {cases.map((supportCase) => {
                const sla = describeCaseSla(supportCase, now);
                const selected = String(supportCase._id) === String(selectedCaseId);
                return (
                  <tr
                    key={supportCase._id}
                    className={`${selected ? "is-selected" : ""}${sla.breached ? " is-breached" : ""}`}
                    onClick={() => openCase(supportCase._id)}
                  >
                    <td>
                      <span className="support-case-number">{supportCase.caseNumber || "Case"}</span>
                      <div className="support-case-meta">
                        {supportCase.createdAt
                          ? moment(supportCase.createdAt).format("MMM D, h:mm A")
                          : ""}
                      </div>
                    </td>
                    <td>
                      <span className="support-case-subject" title={supportCase.subject || ""}>
                        {supportCase.subject || "No subject"}
                      </span>
                    </td>
                    <td>
                      <span className="support-case-meta">
                        {caseCategoryLabel(supportCase.category)}
                      </span>
                    </td>
                    <td>
                      <CaseChip tone={casePriorityTone(supportCase.priority)}>
                        {casePriorityLabel(supportCase.priority)}
                      </CaseChip>
                    </td>
                    <td>
                      <CaseChip tone={caseStatusTone(supportCase.status)}>
                        {caseStatusLabel(supportCase.status)}
                      </CaseChip>
                    </td>
                    <td>
                      <SlaCell supportCase={supportCase} now={now} />
                    </td>
                    <td>
                      <span className="support-case-meta">{supportCase.order?.number || ""}</span>
                    </td>
                    <td>
                      <span className="support-case-meta">{supportCase.vendor?.name || ""}</span>
                    </td>
                    <td>
                      <span className="support-case-meta">
                        {supportCase.assignment?.assigneeName ||
                          caseAssigneeTypeLabel(supportCase.assignment?.assigneeType)}
                      </span>
                    </td>
                    <td>
                      <span className="support-case-meta">
                        {supportCase.lastActivityAt
                          ? moment(supportCase.lastActivityAt).fromNow()
                          : ""}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="support-case-pager">
            <span className="support-case-pager-count">
              {total
                ? `${total.toLocaleString()} case${total === 1 ? "" : "s"}, page ${query.page} of ${pageCount}`
                : "No cases"}
            </span>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => goToPage(query.page - 1)}
                disabled={loading || query.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => goToPage(query.page + 1)}
                disabled={loading || (!hasMore && query.page >= pageCount)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <SupportCaseCreateForm
        show={showCreate}
        onHide={closeCreate}
        onCreated={onCaseCreated}
        isStaff={platformAdmin}
        vocabulary={vocabulary}
      />

      <SupportCaseDetailPanel
        caseId={selectedCaseId}
        show={Boolean(selectedCaseId)}
        onHide={closeCase}
        onCaseChanged={onCaseChanged}
        refreshSignal={detailSignal}
        vocabulary={vocabulary}
      />
    </Container>
  );
};

export default SupportCaseQueue;
